/**
 * Bir kalemin (hammadde/stok) son alış fiyatlarını gösteren ortak drawer.
 *
 * İki yerden kullanılır:
 *  - Stok ekranı: /stock-items/{id}/price-history/ (lokasyon-bazlı tek kalem)
 *  - Reçete (BOM) ekranı: /ingredients/{id}/price-history/ (tüm lokasyonlar
 *    birleşik; satırlar lokasyon adını da taşır)
 *
 * Yalnız mal kabul (alım) hareketleri, en yeni 5. Salt görüntüleme —
 * tedarikçi fiyat trendini izlemek için. Değer ledger'dan (cost_minor_at_time)
 * gelir, buradan düzenlenmez.
 */
import { Alert, Badge, Drawer, Loader, Stack, Table, Text } from "@mantine/core";
import { IconInfoCircle, IconTrendingUp } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api";
import { MoneyDisplay } from "./components";
import { formatQty } from "./format";

export interface PriceHistoryRow {
  id: string;
  created_at: string;
  unit_cost_minor: number;
  quantity_delta: string;
  gr_number: string;
  /** Yalnız ingredient-bazlı (birleşik) yanıtta dolu — StockItem-bazlıda yok. */
  location_name?: string;
  actor_username?: string;
}

export function PriceHistoryDrawer({
  opened,
  onClose,
  title,
  endpoint,
  unit,
}: {
  opened: boolean;
  onClose: () => void;
  /** Drawer başlığındaki kalem adı (ör. "Kıyma"). */
  title: string;
  /** Tam endpoint yolu, ör. `/api/v1/stock-items/{id}/price-history/`.
   *  null/undefined iken sorgu çalışmaz (drawer kapalı). */
  endpoint: string | null;
  /** Miktar birimi (kg, adet...) — satırda gösterilir. */
  unit?: string;
}) {
  const { data, isLoading } = useQuery<PriceHistoryRow[]>({
    queryKey: ["price-history", endpoint],
    queryFn: async () => (await apiClient.get(endpoint!)).data,
    enabled: opened && !!endpoint,
    staleTime: 60_000,
  });

  // En az bir satırda lokasyon varsa lokasyon sütununu göster (Reçete birleşik
  // görünümü). Stok ekranı tek lokasyon olduğundan sütun gizli kalır.
  const showLocation = !!data?.some((r) => r.location_name);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={`📈 ${title} — Son Alış Fiyatları`}
    >
      {isLoading ? (
        <Loader />
      ) : !data || data.length === 0 ? (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
          Bu kalem için henüz mal kabul (alım) kaydı yok. Birim maliyet, ilk
          mal kabul yapıldığında oluşur.
        </Alert>
      ) : (
        <Stack gap="md">
          <Alert color="gray" variant="light" icon={<IconTrendingUp size={18} />}>
            En son {data.length} mal kabulün birim alış fiyatı (en yeni üstte).
            Güncel maliyet bu alımların ağırlıklı ortalamasıdır (AVCO).
          </Alert>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tarih</Table.Th>
                <Table.Th>Mal Girişi</Table.Th>
                {showLocation && <Table.Th>Depo</Table.Th>}
                <Table.Th ta="right">Miktar</Table.Th>
                <Table.Th ta="right">Birim Fiyat</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text size="sm">
                      {new Date(row.created_at).toLocaleDateString("tr-TR", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                      })}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {new Date(row.created_at).toLocaleTimeString("tr-TR", {
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {row.gr_number ? (
                      <Badge variant="light" size="sm">{row.gr_number}</Badge>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  {showLocation && (
                    <Table.Td>
                      <Text size="sm">{row.location_name || "—"}</Text>
                    </Table.Td>
                  )}
                  <Table.Td ta="right">
                    {formatQty(row.quantity_delta)}
                    {unit && <Text span size="xs" c="dimmed"> {unit}</Text>}
                  </Table.Td>
                  <Table.Td ta="right" fw={600}>
                    <MoneyDisplay minor={row.unit_cost_minor} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
    </Drawer>
  );
}
